import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  CreditCard, Truck, MapPin, User, Phone, FileText, 
  ChevronLeft, Loader2, CheckCircle, Package
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import { useCart } from '../contexts/CartContext';
import { PriceDisplay } from '../components/PriceDisplay';
import PaymentMethodPicker from '../components/PaymentMethodPicker';
import AddressMapPicker from '../components/AddressMapPicker';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMIRATES = [
  { value: 'dubai', label_ar: 'دبي', label_en: 'Dubai' },
  { value: 'abu_dhabi', label_ar: 'أبوظبي', label_en: 'Abu Dhabi' },
  { value: 'sharjah', label_ar: 'الشارقة', label_en: 'Sharjah' },
  { value: 'ajman', label_ar: 'عجمان', label_en: 'Ajman' },
  { value: 'ras_al_khaimah', label_ar: 'رأس الخيمة', label_en: 'Ras Al Khaimah' },
  { value: 'fujairah', label_ar: 'الفجيرة', label_en: 'Fujairah' },
  { value: 'umm_al_quwain', label_ar: 'أم القيوين', label_en: 'Umm Al Quwain' }
];

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';
  
  const { cartItems, getCartTotal, clearCart, isLoading: cartLoading } = useCart();
  
  const [step, setStep] = useState(1); // 1: Delivery, 2: Review, 3: Success
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  // paymentMethod kept only so the success screen can echo back what was used.
  const [paymentMethod, setPaymentMethod] = useState('cash_on_delivery');
  
  const [deliveryInfo, setDeliveryInfo] = useState({
    full_name: '',
    phone: '',
    emirate: 'dubai',
    area: '',
    address: '',
    notes: '',
    lat: null,
    lng: null,
  });

  // Saved addresses + address-save intent
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [saveAsDefault, setSaveAsDefault] = useState(false);

  const [errors, setErrors] = useState({});

  const token = localStorage.getItem('token');

  // Load saved addresses — if a default exists, pre-select it.
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/customer/addresses`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const addrs = res.data?.addresses || [];
        setSavedAddresses(addrs);
        const def = addrs.find(a => a.is_default) || addrs[0];
        if (def) {
          setSelectedAddressId(def.id);
          setDeliveryInfo(prev => ({
            ...prev,
            full_name: def.full_name,
            phone: (def.phone || '').replace(/^(\+971|00971|971|0)/, '').trim(),
            emirate: (def.emirate || prev.emirate),
            area: def.area || '',
            address: def.address || '',
            notes: def.notes || '',
            lat: def.lat ?? null,
            lng: def.lng ?? null,
          }));
        }
      } catch (e) { /* ignore — user can still enter manually */ }
    })();
  }, [token]);

  // Pre-fill user info on mount
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const userData = JSON.parse(userStr);
        let phoneNumber = '';
        if (userData.phone) {
          // Remove +971, 00971, 971 prefix or leading 0
          phoneNumber = userData.phone.replace(/^(\+971|00971|971|0)/, '').trim();
        }
        setDeliveryInfo(prev => ({
          ...prev,
          full_name: userData.name || prev.full_name,
          phone: phoneNumber || prev.phone
        }));
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
  }, []);

  // Redirect if cart is empty
  useEffect(() => {
    if (!cartLoading && cartItems.length === 0 && step !== 3) {
      navigate('/cart');
    }
  }, [cartItems, cartLoading, step, navigate]);

  // Require login
  useEffect(() => {
    if (!token) {
      toast.error(isAr ? 'يرجى تسجيل الدخول أولاً' : 'Please login first');
      navigate('/login', { state: { from: '/checkout' } });
    }
  }, [token, navigate, isAr]);

  const subtotal = getCartTotal();
  const deliveryFee = 0; // Free delivery
  const total = subtotal + deliveryFee;

  const validateDeliveryInfo = () => {
    const newErrors = {};
    
    if (!deliveryInfo.full_name.trim()) {
      newErrors.full_name = isAr ? 'الاسم مطلوب' : 'Name is required';
    }
    
    // Phone validation - should be UAE format (9 digits after country code)
    const phoneDigits = deliveryInfo.phone.replace(/\s/g, '');
    if (!phoneDigits) {
      newErrors.phone = isAr ? 'رقم الهاتف مطلوب' : 'Phone is required';
    } else if (!/^\d{9,10}$/.test(phoneDigits)) {
      newErrors.phone = isAr ? 'رقم هاتف غير صالح (9-10 أرقام)' : 'Invalid phone number (9-10 digits)';
    }
    
    if (!deliveryInfo.area.trim()) {
      newErrors.area = isAr ? 'المنطقة مطلوبة' : 'Area is required';
    }
    
    if (!deliveryInfo.address.trim()) {
      newErrors.address = isAr ? 'العنوان مطلوب' : 'Address is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinueToReview = () => {
    if (validateDeliveryInfo()) {
      setStep(2);
      window.scrollTo(0, 0);
    }
  };

  // Online payment flow: create the order first (status=pending_payment)
  // then open a checkout link with the chosen provider (Stripe/Tamara/etc.)
  const _maybePersistAddress = async (formattedPhone) => {
    if (!saveAsDefault || !token) return;
    try {
      await axios.post(
        `${API_URL}/api/customer/addresses`,
        {
          label: isAr ? 'عنوان الشحن' : 'Shipping address',
          full_name: deliveryInfo.full_name,
          phone: formattedPhone,
          emirate: deliveryInfo.emirate,
          area: deliveryInfo.area,
          address: deliveryInfo.address,
          notes: deliveryInfo.notes,
          lat: deliveryInfo.lat,
          lng: deliveryInfo.lng,
          is_default: true,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (_) { /* non-blocking */ }
  };

  const handlePlaceOrder = async () => {
    setIsSubmitting(true);
    try {
      const formattedPhone = '+971' + deliveryInfo.phone.replace(/\s/g, '');
      const orderData = {
        items: cartItems.map(item => ({
          product_id: item.id,
          name: item.name,
          nameEn: item.nameEn,
          price: item.price,
          quantity: item.quantity,
          image: item.image,
          store_id: item.store_id,
          store_name: item.store_name,
        })),
        delivery_info: { ...deliveryInfo, phone: formattedPhone },
        payment_method: 'cash_on_delivery',
        subtotal, delivery_fee: deliveryFee, total,
      };
      const response = await axios.post(
        `${API_URL}/api/customer/orders`,
        orderData,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.success) {
        setOrderResult(response.data);
        clearCart();
        setStep(3);
        window.scrollTo(0, 0);
        _maybePersistAddress(formattedPhone);
      } else {
        throw new Error(response.data.message || 'Order failed');
      }
    } catch (error) {
      console.error('Order error:', error);
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 401 || detail === 'Invalid authentication credentials') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        toast.error(isAr ? 'انتهت صلاحية الجلسة. يرجى تسجيل الدخول مرة أخرى' : 'Session expired. Please login again');
        setTimeout(() => { window.location.href = '/login'; }, 1500);
        return;
      }
      toast.error(detail || error.response?.data?.message || (isAr ? 'حدث خطأ أثناء إنشاء الطلب' : 'Error creating order'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Online payment flow: create pending order first, then redirect to gateway.
  const handlePayOnline = async (provider) => {
    setIsSubmitting(true);
    try {
      const formattedPhone = '+971' + deliveryInfo.phone.replace(/\s/g, '');
      const orderData = {
        items: cartItems.map(item => ({
          product_id: item.id,
          name: item.name,
          nameEn: item.nameEn,
          price: item.price,
          quantity: item.quantity,
          image: item.image,
          store_id: item.store_id,
          store_name: item.store_name
        })),
        delivery_info: { ...deliveryInfo, phone: formattedPhone },
        payment_method: `online_${provider}`,
        subtotal, delivery_fee: deliveryFee, total,
      };
      const orderRes = await axios.post(`${API_URL}/api/customer/orders`, orderData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const orderId = orderRes.data?.order_id || orderRes.data?.id || orderRes.data?._id;
      if (!orderId) throw new Error(isAr ? 'تعذّر إنشاء الطلب' : 'Order creation failed');

      const linkRes = await axios.post(
        `${API_URL}/api/customer/payment/create-link`,
        {
          provider,
          amount: total,
          currency: 'AED',
          description: isAr ? `طلب رقم ${orderId}` : `Order ${orderId}`,
          reference: String(orderId),
          customer_name: deliveryInfo.full_name,
          customer_phone: formattedPhone,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const url = linkRes.data?.checkout_url;
      if (!url) throw new Error(isAr ? 'لم يصل رابط الدفع' : 'No checkout url returned');
      // Persist the reference so /pay/success can display order info
      try { localStorage.setItem('last_payment_reference', String(orderId)); } catch (_) {}
      await _maybePersistAddress(formattedPhone);
      clearCart();
      window.location.href = url;
    } catch (error) {
      console.error('Online payment error:', error);
      const detail = error.response?.data?.detail;
      toast.error(
        (typeof detail === 'string' ? detail : detail?.message_ar || detail?.message_en) ||
        error.message ||
        (isAr ? 'تعذّر بدء عملية الدفع' : 'Failed to start payment'),
      );
      setIsSubmitting(false);
    }
  };

  if (cartLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-[#F8FAFC]'}`}>
        <Loader2 className="h-12 w-12 animate-spin text-[#1E9BD9]" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-[#F8FAFC]'}`} dir={isAr ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className={`sticky top-0 z-40 ${isDarkMode ? 'bg-[#0a0a0a]/90' : 'bg-white/90'} backdrop-blur-md border-b ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <button 
            onClick={() => step === 1 ? navigate('/cart') : setStep(step - 1)}
            className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
            disabled={step === 3}
          >
            <ChevronLeft className={`h-6 w-6 ${isAr ? 'rotate-180' : ''} ${isDarkMode ? 'text-white' : 'text-gray-800'}`} />
          </button>
          <h1 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            {step === 3 
              ? (isAr ? 'تم الطلب بنجاح' : 'Order Placed')
              : (isAr ? 'إتمام الطلب' : 'Checkout')
            }
          </h1>
          <div className="w-10" />
        </div>
      </div>

      {/* Progress Steps */}
      {step !== 3 && (
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-center gap-4">
            <div className={`flex items-center gap-2 ${step >= 1 ? 'text-[#1E9BD9]' : isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                step >= 1 ? 'bg-[#1E9BD9] text-white' : isDarkMode ? 'bg-white/10' : 'bg-gray-200'
              }`}>1</div>
              <span className="text-sm font-medium hidden sm:inline">{isAr ? 'التوصيل' : 'Delivery'}</span>
            </div>
            <div className={`w-12 h-0.5 ${step >= 2 ? 'bg-[#1E9BD9]' : isDarkMode ? 'bg-white/10' : 'bg-gray-200'}`} />
            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-[#1E9BD9]' : isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                step >= 2 ? 'bg-[#1E9BD9] text-white' : isDarkMode ? 'bg-white/10' : 'bg-gray-200'
              }`}>2</div>
              <span className="text-sm font-medium hidden sm:inline">{isAr ? 'المراجعة' : 'Review'}</span>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Step 1: Delivery Info */}
        {step === 1 && (
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className={`rounded-2xl p-6 ${isDarkMode ? 'bg-[#141414] border border-white/10' : 'bg-white'} shadow-lg`}>
                <h2 className={`text-xl font-bold mb-6 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  <Truck className="h-6 w-6 text-[#1E9BD9]" />
                  {isAr ? 'معلومات التوصيل' : 'Delivery Information'}
                </h2>

                {/* Saved addresses selector */}
                {savedAddresses.length > 0 && (
                  <div className="mb-6" data-testid="saved-addresses-selector">
                    <Label className={isDarkMode ? 'text-gray-300' : ''}>
                      {isAr ? 'العناوين المحفوظة' : 'Saved addresses'}
                    </Label>
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {savedAddresses.map(a => {
                        const active = selectedAddressId === a.id;
                        return (
                          <button
                            key={a.id}
                            type="button"
                            data-testid={`saved-address-${a.id}`}
                            onClick={() => {
                              setSelectedAddressId(a.id);
                              setDeliveryInfo(prev => ({
                                ...prev,
                                full_name: a.full_name,
                                phone: (a.phone || '').replace(/^(\+971|00971|971|0)/, '').trim(),
                                emirate: a.emirate || prev.emirate,
                                area: a.area || '',
                                address: a.address || '',
                                notes: a.notes || '',
                                lat: a.lat ?? null,
                                lng: a.lng ?? null,
                              }));
                            }}
                            className={`text-start p-3 rounded-xl border transition-all ${
                              active
                                ? 'border-[#1E9BD9] bg-[#1E9BD9]/10'
                                : (isDarkMode ? 'border-white/10 hover:border-white/20' : 'border-gray-200 hover:border-gray-300')
                            }`}
                          >
                            <div className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                              {a.label} {a.is_default && <span className="text-[10px] font-normal text-[#1E9BD9]">({isAr ? 'افتراضي' : 'default'})</span>}
                            </div>
                            <div className={`text-xs mt-0.5 line-clamp-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{a.address}</div>
                          </button>
                        );
                      })}
                      <button
                        type="button"
                        data-testid="clear-selected-address"
                        onClick={() => {
                          setSelectedAddressId(null);
                          setDeliveryInfo({ full_name: '', phone: '', emirate: 'dubai', area: '', address: '', notes: '', lat: null, lng: null });
                        }}
                        className={`text-start p-3 rounded-xl border-dashed border transition-all text-sm ${isDarkMode ? 'border-white/10 text-gray-400 hover:border-white/20' : 'border-gray-300 text-gray-500 hover:border-gray-400'}`}
                      >
                        {isAr ? '+ عنوان جديد' : '+ New address'}
                      </button>
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  {/* Full Name */}
                  <div>
                    <Label className={isDarkMode ? 'text-gray-300' : ''}>
                      {isAr ? 'الاسم الكامل' : 'Full Name'} *
                    </Label>
                    <div className="relative mt-1">
                      <User className={`absolute start-3 top-1/2 -translate-y-1/2 h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                      <Input
                        value={deliveryInfo.full_name}
                        onChange={(e) => setDeliveryInfo({ ...deliveryInfo, full_name: e.target.value })}
                        className={`ps-10 ${isDarkMode ? 'bg-white/10 border-white/20 text-white' : ''} ${errors.full_name ? 'border-red-500' : ''}`}
                        placeholder={isAr ? 'أدخل اسمك الكامل' : 'Enter your full name'}
                      />
                    </div>
                    {errors.full_name && <p className="text-red-500 text-sm mt-1">{errors.full_name}</p>}
                  </div>

                  {/* Phone */}
                  <div>
                    <Label className={isDarkMode ? 'text-gray-300' : ''}>
                      {isAr ? 'رقم الهاتف' : 'Phone Number'} *
                    </Label>
                    <div className="relative mt-1 flex" dir="ltr">
                      {/* UAE Country Code - Fixed */}
                      <div className={`flex items-center gap-2 px-3 rounded-s-lg border border-e-0 ${
                        isDarkMode 
                          ? 'bg-white/5 border-white/20 text-gray-400' 
                          : 'bg-gray-100 border-gray-200 text-gray-600'
                      }`}>
                        <span className="text-lg">🇦🇪</span>
                        <span className="font-medium">+971</span>
                      </div>
                      <Input
                        type="tel"
                        value={deliveryInfo.phone}
                        onChange={(e) => {
                          // Remove any non-digit characters except spaces
                          const cleaned = e.target.value.replace(/[^\d\s]/g, '');
                          setDeliveryInfo({ ...deliveryInfo, phone: cleaned });
                        }}
                        className={`flex-1 rounded-s-none ${isDarkMode ? 'bg-white/10 border-white/20 text-white placeholder:text-gray-500' : 'text-gray-800'} ${errors.phone ? 'border-red-500' : ''}`}
                        placeholder="50 123 4567"
                        maxLength={12}
                      />
                    </div>
                    {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                  </div>

                  {/* Emirate */}
                  <div>
                    <Label className={isDarkMode ? 'text-gray-300' : ''}>
                      {isAr ? 'الإمارة' : 'Emirate'} *
                    </Label>
                    <select
                      value={deliveryInfo.emirate}
                      onChange={(e) => setDeliveryInfo({ ...deliveryInfo, emirate: e.target.value })}
                      className={`w-full mt-1 p-3 rounded-lg border ${
                        isDarkMode 
                          ? 'bg-white/10 border-white/20 text-white' 
                          : 'bg-white border-gray-200 text-gray-800'
                      }`}
                    >
                      {EMIRATES.map(emirate => (
                        <option key={emirate.value} value={emirate.value} className={isDarkMode ? 'bg-[#141414]' : ''}>
                          {isAr ? emirate.label_ar : emirate.label_en}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Area */}
                  <div>
                    <Label className={isDarkMode ? 'text-gray-300' : ''}>
                      {isAr ? 'المنطقة' : 'Area'} *
                    </Label>
                    <div className="relative mt-1">
                      <MapPin className={`absolute start-3 top-1/2 -translate-y-1/2 h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                      <Input
                        value={deliveryInfo.area}
                        onChange={(e) => setDeliveryInfo({ ...deliveryInfo, area: e.target.value })}
                        className={`ps-10 ${isDarkMode ? 'bg-white/10 border-white/20 text-white' : ''} ${errors.area ? 'border-red-500' : ''}`}
                        placeholder={isAr ? 'مثال: البرشاء، الممزر' : 'e.g., Al Barsha, Mamzar'}
                      />
                    </div>
                    {errors.area && <p className="text-red-500 text-sm mt-1">{errors.area}</p>}
                  </div>

                  {/* Full Address */}
                  <div>
                    <Label className={isDarkMode ? 'text-gray-300' : ''}>
                      {isAr ? 'العنوان التفصيلي' : 'Full Address'} *
                    </Label>
                    <Textarea
                      value={deliveryInfo.address}
                      onChange={(e) => setDeliveryInfo({ ...deliveryInfo, address: e.target.value })}
                      className={`mt-1 ${isDarkMode ? 'bg-white/10 border-white/20 text-white' : ''} ${errors.address ? 'border-red-500' : ''}`}
                      placeholder={isAr ? 'رقم المبنى، اسم الشارع، أي معلومات إضافية' : 'Building number, street name, any additional info'}
                      rows={3}
                    />
                    {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
                  </div>

                  {/* Notes */}
                  <div>
                    <Label className={isDarkMode ? 'text-gray-300' : ''}>
                      {isAr ? 'ملاحظات إضافية' : 'Additional Notes'}
                    </Label>
                    <Textarea
                      value={deliveryInfo.notes}
                      onChange={(e) => setDeliveryInfo({ ...deliveryInfo, notes: e.target.value })}
                      className={`mt-1 ${isDarkMode ? 'bg-white/10 border-white/20 text-white' : ''}`}
                      placeholder={isAr ? 'أي تعليمات خاصة للتوصيل' : 'Any special delivery instructions'}
                      rows={2}
                    />
                  </div>

                  {/* Map pin */}
                  <AddressMapPicker
                    isAr={isAr}
                    isDarkMode={isDarkMode}
                    lat={deliveryInfo.lat}
                    lng={deliveryInfo.lng}
                    onChange={({ lat, lng }) => setDeliveryInfo({ ...deliveryInfo, lat, lng })}
                  />

                  {/* Save as default toggle */}
                  <label className="flex items-center gap-2 cursor-pointer select-none pt-2" data-testid="save-as-default-toggle">
                    <input
                      type="checkbox"
                      checked={saveAsDefault}
                      onChange={e => setSaveAsDefault(e.target.checked)}
                      className="h-4 w-4 accent-[#1E9BD9]"
                    />
                    <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {isAr ? 'حفظ هذا العنوان كعنوان افتراضي في ملفي الشخصي' : 'Save this address as default in my profile'}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Order Summary Sidebar */}
            <div className="lg:col-span-1">
              <div className={`rounded-2xl p-6 sticky top-32 ${isDarkMode ? 'bg-[#141414] border border-white/10' : 'bg-white'} shadow-lg`}>
                <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {isAr ? 'ملخص الطلب' : 'Order Summary'}
                </h3>

                <div className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {cartItems.length} {isAr ? 'منتج' : 'items'}
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                      {isAr ? 'المجموع الفرعي' : 'Subtotal'}
                    </span>
                    <span className={isDarkMode ? 'text-white' : 'text-gray-800'}>
                      <PriceDisplay price={subtotal} />
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                      {isAr ? 'التوصيل' : 'Delivery'}
                    </span>
                    <span className="text-green-500">
                      {isAr ? 'مجاني' : 'Free'}
                    </span>
                  </div>
                  <div className={`pt-3 border-t ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
                    <div className="flex justify-between">
                      <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        {isAr ? 'الإجمالي' : 'Total'}
                      </span>
                      <span className="text-xl font-bold text-[#1E9BD9]">
                        <PriceDisplay price={total} />
                      </span>
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={handleContinueToReview}
                  className="w-full py-6 text-lg font-semibold rounded-xl bg-gradient-to-r from-[#1E9BD9] to-[#0284C7] hover:opacity-90"
                >
                  {isAr ? 'متابعة' : 'Continue'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Review Order */}
        {step === 2 && (
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              {/* Delivery Summary */}
              <div className={`rounded-2xl p-6 ${isDarkMode ? 'bg-[#141414] border border-white/10' : 'bg-white'} shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className={`text-lg font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    <Truck className="h-5 w-5 text-[#1E9BD9]" />
                    {isAr ? 'عنوان التوصيل' : 'Delivery Address'}
                  </h2>
                  <button 
                    onClick={() => setStep(1)}
                    className="text-[#1E9BD9] text-sm hover:underline"
                  >
                    {isAr ? 'تعديل' : 'Edit'}
                  </button>
                </div>
                <div className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                  <p className="font-medium">{deliveryInfo.full_name}</p>
                  <p dir="ltr" className="text-start">+971{deliveryInfo.phone.replace(/\s/g, '')}</p>
                  <p>{deliveryInfo.address}</p>
                  <p>{deliveryInfo.area}, {EMIRATES.find(e => e.value === deliveryInfo.emirate)?.[isAr ? 'label_ar' : 'label_en']}</p>
                  {deliveryInfo.notes && <p className="mt-2 text-sm italic">"{deliveryInfo.notes}"</p>}
                </div>
              </div>

              {/* Payment Method — unified picker (COD + online gateways) */}
              <div className={`rounded-2xl p-6 ${isDarkMode ? 'bg-[#141414] border border-white/10' : 'bg-white'} shadow-lg`}>
                <h2 className={`text-lg font-bold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  <CreditCard className="h-5 w-5 text-[#1E9BD9]" />
                  {isAr ? 'طريقة الدفع' : 'Payment Method'}
                </h2>

                <PaymentMethodPicker
                  amount={total}
                  currency="AED"
                  isAr={isAr}
                  disabled={isSubmitting}
                  includeCod={true}
                  onConfirm={({ method, provider }) => {
                    setPaymentMethod(method === 'cash_on_delivery' ? 'cash_on_delivery' : `online_${provider}`);
                    if (method === 'cash_on_delivery') handlePlaceOrder();
                    else handlePayOnline(provider);
                  }}
                />
              </div>

              {/* Order Items */}
              <div className={`rounded-2xl p-6 ${isDarkMode ? 'bg-[#141414] border border-white/10' : 'bg-white'} shadow-lg`}>
                <h2 className={`text-lg font-bold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  <Package className="h-5 w-5 text-[#1E9BD9]" />
                  {isAr ? 'المنتجات' : 'Items'} ({cartItems.length})
                </h2>
                <div className="space-y-3">
                  {cartItems.map(item => (
                    <div key={item.id} className={`flex gap-4 py-3 ${isDarkMode ? 'border-b border-white/10' : 'border-b'} last:border-0`}>
                      <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden">
                        {item.image ? (
                          <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className={`w-full h-full flex items-center justify-center ${isDarkMode ? 'bg-white/10' : 'bg-gray-100'}`}>
                            <Package className={`h-6 w-6 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                          {isAr ? item.name : item.nameEn}
                        </h4>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {isAr ? 'الكمية:' : 'Qty:'} {item.quantity}
                        </p>
                      </div>
                      <div className="text-end">
                        <p className="font-bold text-[#1E9BD9]">
                          <PriceDisplay price={item.price * item.quantity} />
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Final Summary */}
            <div className="lg:col-span-1">
              <div className={`rounded-2xl p-6 sticky top-32 ${isDarkMode ? 'bg-[#141414] border border-white/10' : 'bg-white'} shadow-lg`}>
                <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {isAr ? 'ملخص الطلب' : 'Order Summary'}
                </h3>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                      {isAr ? 'المجموع الفرعي' : 'Subtotal'}
                    </span>
                    <span className={isDarkMode ? 'text-white' : 'text-gray-800'}>
                      <PriceDisplay price={subtotal} />
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                      {isAr ? 'التوصيل' : 'Delivery'}
                    </span>
                    <span className="text-green-500">
                      {isAr ? 'مجاني' : 'Free'}
                    </span>
                  </div>
                  <div className={`pt-3 border-t ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
                    <div className="flex justify-between">
                      <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        {isAr ? 'الإجمالي' : 'Total'}
                      </span>
                      <span className="text-2xl font-bold text-[#1E9BD9]">
                        <PriceDisplay price={total} />
                      </span>
                    </div>
                  </div>
                </div>

                {/* The unified PaymentMethodPicker above already has its own
                    "تأكيد وإتمام الدفع" confirm button, so no second CTA here. */}

                <p className={`text-xs text-center mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {isAr
                    ? 'بالضغط على زر التأكيد / المتابعة أنت توافق على شروط الاستخدام'
                    : 'By placing the order you agree to our Terms of Service'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Success */}
        {step === 3 && orderResult && (
          <div className={`max-w-lg mx-auto rounded-2xl p-8 text-center ${isDarkMode ? 'bg-[#141414] border border-white/10' : 'bg-white'} shadow-lg`}>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="h-12 w-12 text-green-500" />
            </div>
            
            <h1 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {isAr ? 'تم الطلب بنجاح!' : 'Order Placed Successfully!'}
            </h1>
            
            <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {isAr ? 'شكراً لك! سيتم التواصل معك قريباً' : 'Thank you! We will contact you soon'}
            </p>

            <div className={`p-4 rounded-xl mb-6 ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {isAr ? 'رقم الطلب' : 'Order Number'}
              </p>
              <p className="text-2xl font-bold text-[#1E9BD9] font-mono">
                {orderResult.order_id}
              </p>
            </div>

            <div className={`text-start p-4 rounded-xl mb-6 ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
              <div className="flex justify-between mb-2">
                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                  {isAr ? 'الإجمالي' : 'Total'}
                </span>
                <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  <PriceDisplay price={orderResult.total || total} />
                </span>
              </div>
              <div className="flex justify-between">
                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                  {isAr ? 'طريقة الدفع' : 'Payment'}
                </span>
                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                  {paymentMethod === 'cash_on_delivery'
                    ? (isAr ? 'الدفع عند الاستلام' : 'Cash on Delivery')
                    : (isAr
                        ? `الدفع عبر ${paymentMethod.replace('online_', '').toUpperCase()}`
                        : `Paid via ${paymentMethod.replace('online_', '').toUpperCase()}`)}
                </span>
              </div>
            </div>

            <div className="space-y-3">
              <Link to="/dashboard?tab=orders">
                <Button className="w-full py-4 bg-[#1E9BD9] hover:bg-[#1789C2]">
                  <FileText className="h-5 w-5 me-2" />
                  {isAr ? 'تتبع الطلب' : 'Track Order'}
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className={`w-full py-4 ${isDarkMode ? 'border-white/20 hover:bg-white/5' : ''}`}>
                  {isAr ? 'العودة للرئيسية' : 'Back to Home'}
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
